import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

const AccessibilityContext = createContext(null);

const STORAGE_KEY = "clearpath:a11y";

function readSettings() {
    if (typeof window === "undefined")
        return { highContrast: false, textScale: 1, quietMode: false };
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return { highContrast: false, textScale: 1, quietMode: false };
        const parsed = JSON.parse(raw);
        return {
            highContrast: !!parsed.highContrast,
            textScale: [1, 1.25, 1.5].includes(parsed.textScale)
                ? parsed.textScale
                : 1,
            quietMode: !!parsed.quietMode,
        };
    } catch (_) {
        return { highContrast: false, textScale: 1, quietMode: false };
    }
}

const QUIET_WINDOW = { start: 22, end: 7 }; // 10pm – 7am

export function isQuietNow() {
    const h = new Date().getHours();
    if (QUIET_WINDOW.start <= QUIET_WINDOW.end) {
        return h >= QUIET_WINDOW.start && h < QUIET_WINDOW.end;
    }
    return h >= QUIET_WINDOW.start || h < QUIET_WINDOW.end;
}

export { QUIET_WINDOW };

export function AccessibilityProvider({ children }) {
    const [highContrast, setHighContrast] = useState(
        () => readSettings().highContrast,
    );
    const [textScale, setTextScale] = useState(() => readSettings().textScale);
    const [quietMode, setQuietMode] = useState(() => readSettings().quietMode);

    // Apply to <html> + persist
    useEffect(() => {
        if (typeof document === "undefined") return;
        const root = document.documentElement;
        root.classList.toggle("hc-mode", highContrast);
        root.classList.toggle("quiet-mode", quietMode);
        root.dataset.cpTextscale = String(textScale);
        try {
            window.localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ highContrast, textScale, quietMode }),
            );
        } catch (err) {
            if (typeof console !== "undefined") {
                console.warn(
                    "[AccessibilityContext] failed to persist settings:",
                    err,
                );
            }
        }
    }, [highContrast, textScale, quietMode]);

    const toggleHighContrast = useCallback(
        () => setHighContrast((v) => !v),
        [],
    );

    const cycleTextScale = useCallback(() => {
        setTextScale((v) => (v === 1 ? 1.25 : v === 1.25 ? 1.5 : 1));
    }, []);

    const toggleQuietMode = useCallback(() => setQuietMode((v) => !v), []);

    const value = useMemo(
        () => ({
            highContrast,
            textScale,
            quietMode,
            quietActive: quietMode, // (could combine with isQuietNow() for time-gated)
            toggleHighContrast,
            cycleTextScale,
            toggleQuietMode,
            setHighContrast,
            setTextScale,
            setQuietMode,
        }),
        [
            highContrast,
            textScale,
            quietMode,
            toggleHighContrast,
            cycleTextScale,
            toggleQuietMode,
        ],
    );

    return (
        <AccessibilityContext.Provider value={value}>
            {children}
        </AccessibilityContext.Provider>
    );
}

export function useAccessibility() {
    const ctx = useContext(AccessibilityContext);
    if (!ctx)
        throw new Error(
            "useAccessibility must be used within AccessibilityProvider",
        );
    return ctx;
}
